import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import MeetingFrame from "../../../components/MeetingFrame";
import moment from "moment";
import toast from "react-hot-toast";
import API from "../../../Network/API";
import UserAvatar from "../../../components/UserAvatar";
import "./LiveRoom.css";

export const liveRoomLink = (code) => `${window.location.origin}/live/${code}`;

// Teacher's Live Room: start an ad-hoc meeting, share its link, see who joined.
export default function LiveRoom() {
    const { t } = useTranslation();
    const location = useLocation();
    const { user } = useSelector((state) => state.authReducer);
    const prefix = location.pathname.startsWith("/app") ? "/app" : "";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState("");
    const [busy, setBusy] = useState(false);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        return API.get(`/v1/oauth/teacher/live-rooms`)
            .then((response) => setData(response.data.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    // Keep the "who joined" list fresh while a meeting is running.
    const activeCode = data?.active?.code;
    useEffect(() => {
        if (!activeCode) return undefined;
        const timer = setInterval(load, 20000);
        return () => clearInterval(timer);
    }, [activeCode, load]);

    const start = () => {
        setBusy(true);
        API.post(`/v1/oauth/teacher/live-rooms`, { title })
            .then(() => { setTitle(""); return load(); })
            .catch((err) => toast.error(err?.response?.data?.message || t("liveRoom.start_failed")))
            .finally(() => setBusy(false));
    };

    const end = () => {
        if (!data?.active || !window.confirm(t("liveRoom.confirm_end"))) return;
        setBusy(true);
        API.post(`/v1/oauth/teacher/live-rooms/${data.active.id}/end`)
            .then(() => load())
            .catch(() => toast.error(t("liveRoom.end_failed")))
            .finally(() => setBusy(false));
    };

    const copyLink = async (code) => {
        const link = liveRoomLink(code);
        try {
            await navigator.clipboard.writeText(link);
            toast.success(t("liveRoom.link_copied"));
        } catch (e) {
            window.prompt(t("liveRoom.copy_this_link"), link);
        }
    };

    const shareLink = async (code) => {
        if (!navigator.share) return copyLink(code);
        try {
            await navigator.share({ title: t("liveRoom.share_title", { name: user?.name }), url: liveRoomLink(code) });
        } catch (e) { /* dismissed */ }
    };

    if (loading) {
        return <div className="lr-loading"><span className="spinner-border spinner-border-sm me-2" />{t("liveRoom.loading")}</div>;
    }

    const active = data?.active;
    const history = data?.history || [];

    return (
        <div className="lr-page">
            <div className="lr-head">
                <div>
                    <h4 className="lr-title"><i className="fa-light fa-video me-2" />{t("liveRoom.title")}</h4>
                    <p className="lr-sub">{t("liveRoom.subtitle")}</p>
                </div>
                {data?.subscribed && !active &&
                    <div className="lr-start">
                        <input
                            type="text"
                            className="form-control"
                            maxLength={120}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t("liveRoom.title_placeholder")}
                            onKeyDown={(e) => e.key === "Enter" && !busy && start()}
                        />
                        <button type="button" className="btn lr-btn-primary" onClick={start} disabled={busy}>
                            {busy ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="fa-light fa-circle-play me-2" />}
                            {t("liveRoom.start_new")}
                        </button>
                    </div>
                }
            </div>

            {!data?.subscribed &&
                <div className="lr-locked">
                    <i className="fa-light fa-lock-keyhole" />
                    <h5>{t("liveRoom.locked_title")}</h5>
                    <p>{t("liveRoom.locked_text")}</p>
                    <Link to={`${prefix}/dashboard/subscription`} className="btn lr-btn-primary">{t("liveRoom.see_plans")}</Link>
                </div>
            }

            {active &&
                <section className="lr-active">
                    <div className="lr-share">
                        <div className="lr-share-info">
                            <span className="lr-live-dot" />
                            <div>
                                <strong>{active.title || t("liveRoom.untitled")}</strong>
                                <span>{t("liveRoom.started_at", { time: moment(active.started_at).format("h:mm A") })} · {t("liveRoom.joined_count", { count: active.participants_count })}</span>
                            </div>
                        </div>
                        <div className="lr-share-link">
                            <label>{t("liveRoom.share_label")}</label>
                            <div className="lr-link-box">
                                <input type="text" readOnly value={liveRoomLink(active.code)} onFocus={(e) => e.target.select()} />
                                <button type="button" className="btn lr-btn-primary" onClick={() => copyLink(active.code)}>
                                    <i className="fa-light fa-copy me-2" />{t("liveRoom.copy")}
                                </button>
                                <button type="button" className="btn lr-btn-ghost" onClick={() => shareLink(active.code)} aria-label={t("liveRoom.share")}>
                                    <i className="fa-light fa-share-nodes" />
                                </button>
                            </div>
                            <small>{t("liveRoom.share_hint")}</small>
                        </div>
                        <button type="button" className="btn lr-btn-danger" onClick={end} disabled={busy}>
                            <i className="fa-light fa-phone-hangup me-2" />{t("liveRoom.end")}
                        </button>
                    </div>

                    <div className="lr-meeting">
                        <MeetingFrame key={active.jitsi_room} roomName={active.jitsi_room} displayName={user?.name} isHost />
                    </div>

                    {active.participants?.length > 0 &&
                        <div className="lr-joined">
                            <span className="lr-joined-label">{t("liveRoom.joined_so_far")}</span>
                            {active.participants.map((p, i) => (
                                <span className="lr-chip" key={i} title={moment(p.first_joined_at).format("h:mm A")}>
                                    <UserAvatar src={p.avatar} name={p.name} size={22} />{p.name}
                                </span>
                            ))}
                        </div>
                    }
                </section>
            }

            {data?.subscribed &&
                <section className="lr-history">
                    <h5 className="lr-section-title">{t("liveRoom.previous_meetings")}</h5>
                    {history.length === 0 ?
                        <div className="lr-empty"><i className="fa-light fa-calendar-clock" />{t("liveRoom.no_history")}</div>
                        :
                        <div className="lr-list">
                            {history.map((room) => (
                                <div className={`lr-item ${openId === room.id ? "is-open" : ""}`} key={room.id}>
                                    <button type="button" className="lr-item-head" onClick={() => setOpenId(openId === room.id ? null : room.id)} aria-expanded={openId === room.id}>
                                        <span className="lr-item-icon"><i className="fa-light fa-video" /></span>
                                        <span className="lr-item-main">
                                            <strong>{room.title || t("liveRoom.untitled")}</strong>
                                            <span>{moment(room.started_at).format("ddd, D MMM YYYY · h:mm A")} · {t("liveRoom.duration_min", { count: room.duration_minutes })}</span>
                                        </span>
                                        <span className="lr-item-count"><i className="fa-light fa-users me-1" />{room.participants_count}</span>
                                        <i className="fa-light fa-chevron-down lr-item-chevron" />
                                    </button>
                                    {openId === room.id &&
                                        <div className="lr-item-body">
                                            {room.participants?.length > 0 ?
                                                <table className="lr-table">
                                                    <thead>
                                                        <tr><th>{t("liveRoom.student")}</th><th>{t("liveRoom.first_joined")}</th><th>{t("liveRoom.last_joined")}</th><th>{t("liveRoom.joins")}</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {room.participants.map((p, i) => (
                                                            <tr key={i}>
                                                                <td data-label={t("liveRoom.student")}><span className="lr-person"><UserAvatar src={p.avatar} name={p.name} size={28} /><span><b>{p.name}</b><small>{p.email}</small></span></span></td>
                                                                <td data-label={t("liveRoom.first_joined")}>{moment(p.first_joined_at).format("h:mm A")}</td>
                                                                <td data-label={t("liveRoom.last_joined")}>{moment(p.last_joined_at).format("h:mm A")}</td>
                                                                <td data-label={t("liveRoom.joins")}>{p.join_count}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                :
                                                <p className="lr-none">{t("liveRoom.nobody_joined")}</p>
                                            }
                                        </div>
                                    }
                                </div>
                            ))}
                        </div>
                    }
                </section>
            }
        </div>
    );
}
